'use client';

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import RelatedBlogCarousel from '@/components/RelatedBlogCarousel';

export default function BlogPost() {
  const [particles, setParticles] = useState<Array<{ x: number; y: number }>>([]);

  useEffect(() => {
    // Initialize particles only on client side
    setParticles(
      [...Array(50)].map(() => ({
        x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
        y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000)
      }))
    );
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden -mt-4">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] bg-[length:40px_40px] opacity-[0.04]"></div>
        <div className="absolute inset-0 bg-[url('/assets/noise.svg')] opacity-[0.02] mix-blend-overlay"></div>
        
        {/* Animated glowing orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-gradient-to-br from-primary-500/30 to-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
        </div>

        {/* Binary data particles */}
        <div className="absolute inset-0 overflow-hidden">
          {particles.map((particle, i) => (
            <motion.div
              key={i}
              className="absolute text-white/10 font-mono text-sm"
              initial={{
                x: particle.x,
                y: particle.y,
                opacity: 0
              }}
              animate={{
                y: [null, -100],
                opacity: [0, 1, 0],
                x: [null, particle.x + (Math.random() * 100 - 50)]
              }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                delay: Math.random() * 5,
                ease: "linear"
              }}
            >
              {Math.random() > 0.5 ? '1' : '0'}
            </motion.div>
          ))}
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Why Security Should Be a <span className="text-transparent bg-clip-text bg-gradient-rainbow">Top Priority When Choosing a Hosting Service</span>
            </motion.h1>
            <motion.div 
              className="text-xl text-slate-300 max-w-3xl mx-auto flex items-center justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <p>By 1 Dollar Cloud</p>
              <p>Published On: November 28, 2024</p>
              <p>Categories: Security</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Illustration */}
      <section className="py-12 relative overflow-hidden bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <img
                      src="/assets/blog-images/hosting-security.svg"
                      alt="Website Security Illustration"
                      className="w-full h-[500px] object-contain"
                    />
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-20 relative overflow-hidden bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="prose prose-invert max-w-none"
          >
            <div className="bg-slate-800/50 rounded-xl p-8 mb-8">
              <p className="text-slate-300">In today's digital landscape, cybersecurity threats are becoming increasingly sophisticated and frequent. When choosing a hosting service for your website or application, security should be at the forefront of your decision-making process. This comprehensive guide explores why security is crucial and what features to look for in a secure hosting provider.</p>
            </div>

            {[
              {
                title: "The Growing Importance of Web Security",
                content: [
                  "**Rising Cyber Threats:** The frequency and sophistication of cyber attacks continue to increase, making robust security measures essential for any online presence.",
                  "**Data Protection Regulations:** With strict data protection laws like GDPR and CCPA, businesses must ensure their hosting providers comply with security standards.",
                  "**Cost of Security Breaches:** The financial impact of a security breach can be devastating, including:",
                  "• Direct financial losses",
                  "• Damage to brand reputation",
                  "• Legal consequences",
                  "• Loss of customer trust"
                ]
              },
              {
                title: "Essential Security Features to Look For",
                content: [
                  "**1. SSL/TLS Certificates:**",
                  "• Encrypts data transmission",
                  "• Builds customer trust",
                  "• Improves SEO rankings",
                  "**2. Firewall Protection:**",
                  "• Web Application Firewall (WAF)",
                  "• DDoS protection",
                  "• Network monitoring",
                  "**3. Regular Security Updates:**",
                  "• Automated patch management",
                  "• System updates",
                  "• Security vulnerability fixes"
                ]
              },
              {
                title: "Backup and Recovery Solutions",
                content: [
                  "**Comprehensive Backup Systems:**",
                  "• Automated daily backups",
                  "• Multiple backup locations",
                  "• Point-in-time recovery options",
                  "**Disaster Recovery Planning:**",
                  "• Quick restoration capabilities",
                  "• Business continuity assurance",
                  "• Regular backup testing"
                ]
              },
              {
                title: "Access Control and Authentication",
                content: [
                  "**Secure Access Management:**",
                  "• Two-factor authentication (2FA)",
                  "• IP-based access restrictions",
                  "• Strong password policies",
                  "**User Permission Controls:**",
                  "• Role-based access control",
                  "• Audit logging",
                  "• Session management"
                ]
              },
              {
                title: "Malware Protection and Monitoring",
                content: [
                  "**Proactive Security Measures:**",
                  "• Real-time malware scanning",
                  "• Virus protection",
                  "• File integrity monitoring",
                  "**24/7 Security Monitoring:**",
                  "• Intrusion detection systems",
                  "• Security incident response",
                  "• Continuous threat assessment"
                ]
              },
              {
                title: "Data Center Security",
                content: [
                  "**Physical Security Measures:**",
                  "• 24/7 surveillance",
                  "• Biometric access controls",
                  "• Security personnel",
                  "**Infrastructure Security:**",
                  "• Redundant power systems",
                  "• Climate control",
                  "• Fire suppression systems"
                ]
              },
              {
                title: "Compliance and Certifications",
                content: [
                  "**Industry Standards:**",
                  "• ISO 27001 certification",
                  "• PCI DSS compliance",
                  "• SOC 2 certification",
                  "**Regular Audits:**",
                  "• Third-party security audits",
                  "• Compliance monitoring",
                  "• Security policy updates"
                ]
              },
              {
                title: "1 Dollar Cloud's Security Commitment",
                content: [
                  "**Our Security Features:**",
                  "• Free SSL certificates",
                  "• Advanced firewall protection",
                  "• Regular security audits",
                  "• 24/7 monitoring and support",
                  "**Customer Benefits:**",
                  "• Peace of mind",
                  "• Professional security management",
                  "• Cost-effective security solutions"
                ]
              }
            ].map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-800/50 rounded-xl p-8 mb-8"
              >
                <h2 className="text-2xl font-bold text-white mb-6">{section.title}</h2>
                {section.content.map((paragraph, i) => (
                  <p key={i} className="text-slate-300 mb-4" dangerouslySetInnerHTML={{ __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}></p>
                ))}
              </motion.div>
            ))}

            <div className="bg-slate-800/50 rounded-xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-white mb-6">Conclusion</h2>
              <p className="text-slate-300 mb-4">Security should never be an afterthought when choosing a hosting provider. The right security measures can protect your website, your data, and your users from increasingly sophisticated cyber threats.</p>
              <p className="text-slate-300 mb-4">At <strong>1 Dollar Cloud</strong>, we prioritize security in all our hosting solutions, providing you with the protection and peace of mind you need to focus on growing your online presence.</p>
              <p className="text-slate-300">Remember, investing in secure hosting is not just about protecting your website - it's about protecting your business's future and maintaining the trust of your users.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Blog Carousel */}
      <section className="py-8 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RelatedBlogCarousel currentSlug="why-security-should-be-a-top-priority-when-choosing-a-hosting-service" />
        </div>
      </section>
    </div>
  );
}